// src/utils/pageTitle.js
//
// The one place that knows how a page's <title> tag is built.
//
// Why this file exists (s76). The rule was always "append the brand, but
// only when it fits inside the 60-char SERP budget." The legacy useSeo()
// hook implemented it correctly and documented it: skip the suffix when
// metaTitle already contains the brand, and skip it when metaTitle.length
// + 23 would break 60. When s70 migrated head management to <Seo> and
// react-helmet-async, the new component rewrote the line as an
// unconditional `${title} — ${SITE_NAME}` and both guards were lost. From
// that session on, every article, state, guide and blog page rendered a
// <title> over 60 characters, and seven pages rendered the brand twice.
//
// It stayed invisible because the audit measured the `title` PROP rather
// than the rendered tag — it reported 71 while 351 routes were failing.
// s73 taught the audit to measure the tag by regex-parsing the template
// out of Seo.jsx. That was the right instinct aimed at the wrong artifact:
// a regex over a component's source is a copy of the rule that can drift
// from the rule.
//
// So: one exported function, imported by the component that renders titles
// AND by every script that audits or probes them. A script that
// re-implements this rule is a script that can disagree with the site.
// Import it; never retype it.

export const SITE_NAME = 'Window Washing Guide';

// Google truncates desktop SERP titles around 60 characters, and mobile is
// tighter still. 60 is the number every audit and probe in this repo gates on.
export const TITLE_MAX = 60;

// 23 characters, counting the leading space and the em dash.
export const BRAND_SUFFIX = ` — ${SITE_NAME}`;

/**
 * Build the string that belongs in <title>.
 *
 * @param {string} [title] The page's own title — the `title` prop passed to
 *   <Seo>, which pages source from frontmatter `metaTitle` (preferred) or
 *   `title` (fallback).
 * @returns {string} The rendered title, branded only when branding fits.
 */
export function buildPageTitle(title) {
  const t = title == null ? '' : String(title).trim();

  // No title of its own: the brand IS the title. Homepage fallback.
  if (!t) return SITE_NAME;

  // Already carries the brand. Appending again renders
  // "Contact — Window Washing Guide — Window Washing Guide".
  if (t.includes(SITE_NAME)) return t;

  // Brand it only if the branded form stays inside the budget. When it
  // doesn't, the page loses the suffix rather than its own words: in a
  // 60-character window the page's subject outranks the masthead.
  return t.length + BRAND_SUFFIX.length <= TITLE_MAX ? t + BRAND_SUFFIX : t;
}
